import { DateChoiceErrorCodeEnum } from "~/utils/enums/date-choice.enums";

/** Shown when the rejection carries no machine code: there is nothing to tell the admin. */
export const DATE_CHOICE_DEFAULT_ERROR = "Une erreur est survenue";

/** French copy of the machine codes; the English body of the API is never shown. */
const CODE_ERROR_MESSAGES: Record<DateChoiceErrorCodeEnum, string> = {
  [DateChoiceErrorCodeEnum.ALREADY_RESOLVED]: "Participant(s) déjà inscrit(s) à cette session.",
  [DateChoiceErrorCodeEnum.REGISTRATION_FAILED]: "Participant(s) déjà inscrit(s) à cette session.",
  [DateChoiceErrorCodeEnum.SESSION_FULL]: "La dernière place de cette session vient d'être prise.",
  [DateChoiceErrorCodeEnum.SESSION_NOT_ELIGIBLE]: "Cette session n'est plus disponible.",
  [DateChoiceErrorCodeEnum.CARRIER_TRAINING_NOT_FOUND]: "Cette formation n'est plus disponible.",
};

/**
 * French copy of a rejected date-choice call, read from the machine code it carries — the `reason`
 * of a skipped bulk row, or the `message` of an error body.
 *
 * Never keyed on the HTTP status: `BadRequestExceptionFilter` answers **400** for every one of these
 * codes, while the catch-all `GenericExceptionsFilter` turns anything *unmapped* into a 422. A
 * status therefore cannot tell two refusals apart, and reading a 422 as a business refusal labels an
 * internal failure « déjà inscrits ». Keying on the status is what made every single-row rejection
 * read « Une erreur est survenue ».
 */
export function dateChoiceErrorMessage(code: unknown): string {
  const raw = typeof code === "string" ? code : "";
  const matched = Object.values(DateChoiceErrorCodeEnum).find((value) => raw.includes(value));
  return matched ? CODE_ERROR_MESSAGES[matched] : DATE_CHOICE_DEFAULT_ERROR;
}

/**
 * French copy of a rejected request. `useSDKApi` now normalises every rejection onto `message`, so
 * that is where the code usually is; the other two shapes are still read, because they still occur.
 * A bulk row hands its `reason` in as a bare string, and a raw SDK `ResponseError` (one raised
 * outside the proxy) carries the code only in its unread body. Reading all three is the whole point
 * — without it, every rejection of a `pending-registrations` endpoint came out as
 * « Une erreur est survenue ».
 */
export async function dateChoiceRequestError(error: unknown): Promise<string> {
  if (typeof error === "string") return dateChoiceErrorMessage(error);

  const response = (error as { response?: Response })?.response;
  const body = await response?.json?.().catch(() => null);
  return dateChoiceErrorMessage(body?.message ?? (error as { message?: unknown })?.message);
}
